import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '@services/apiClient'
import {
  createGroup,
  deleteGroup,
  listGroups,
  updateGroup,
  type EquivalentDomainGroupWire,
  type UpsertGroupBody,
} from '../api/equivalentDomainsApi'

const messageKey = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'settings.domain.error.no_session'
    if (err.status === 403) return 'settings.domain.error.limit_reached'
    if (err.status === 404) return 'settings.domain.error.not_found'
    if (err.status === 429) return 'settings.domain.error.too_many_requests'
    if (err.status === 400) return 'settings.domain.error.bad_request'

    return 'settings.domain.error.network'
  }

  return 'settings.domain.error.generic'
}

export type DomainsState =
  | { kind: 'loading' }
  | { kind: 'ready'; groups: EquivalentDomainGroupWire[] }
  | { kind: 'error'; messageKey: string }

export interface UseEquivalentDomains {
  state: DomainsState
  lastErrorKey: string | null
  refresh: () => Promise<void>
  create: (body: UpsertGroupBody) => Promise<void>
  update: (id: string, body: UpsertGroupBody) => Promise<void>
  remove: (id: string) => Promise<void>
}

export const useEquivalentDomains = (): UseEquivalentDomains => {
  const [state, setState] = useState<DomainsState>({ kind: 'loading' })
  const [lastErrorKey, setLastErrorKey] = useState<string | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    setLastErrorKey(null)
    try {
      const groups = await listGroups()

      setState({ kind: 'ready', groups })
    } catch (err: unknown) {
      const key = messageKey(err)

      setState({ kind: 'error', messageKey: key })
      setLastErrorKey(key)
    }
  }, [])

  const wrap = useCallback(
    async (fn: () => Promise<void>): Promise<void> => {
      setLastErrorKey(null)
      try {
        await fn()
      } catch (err: unknown) {
        setLastErrorKey(messageKey(err))

        return
      }
      await refresh()
    },
    [refresh],
  )

  const create = useCallback(
    async (body: UpsertGroupBody) =>
      wrap(async () => {
        await createGroup(body)
      }),
    [wrap],
  )

  const update = useCallback(
    async (id: string, body: UpsertGroupBody) =>
      wrap(async () => {
        await updateGroup(id, body)
      }),
    [wrap],
  )

  const remove = useCallback(
    async (id: string) =>
      wrap(async () => {
        await deleteGroup(id)
      }),
    [wrap],
  )

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { state, lastErrorKey, refresh, create, update, remove }
}
